import cx from 'classnames'
import InlineSvg from 'svg-inline-react'
import { pluralize } from 'humanize-plus'
import { Link } from 'react-router'

import { Button, Input, InlineWarning } from '@driftt/tide-core-legacy'
import { PlaybookStatus } from 'app/modules/playbook/constants'
import LoadingDots from 'components/shared/LoadingDots'
import PlaybookDraftToggle from '../PlaybookDraftToggle'
import SendButton from '../SendButton'
import ScheduleButton from '../ScheduleButton'
import OutboundSequenceReview from './OutboundSequence'
import ScheduleEmail from '../ScheduleEmail'
import PlaybookSelects from '../PlaybookSelects'

import needsAttentionIconSrc from '!svg-inline?removeTags=true!images/svg/action-error.svg'

import './style.styl'

const HAS_UNLIMITED_CONTACTS_COPY =
  "Once you turn this playbook on, all contacts in the segment added by the bot will immediately receive emails. " +
  "New contacts added into that same segment will automatically receive these emails."

const ReviewHeader = ({
  playbookStatus,
  isEmailPlaybook,
  isOutboundSequencePlaybook,
  targetingCardinality,
  isCardinalityLoading,
  isOneTimeEmailPlaybook,
  copy,
  hasLimitedContactsMktEmail
}) => (
  <header className="playbook-title-header">
    {isEmailPlaybook &&
      !isOutboundSequencePlaybook &&
      playbookStatus !== PlaybookStatus.SENT &&
      hasLimitedContactsMktEmail &&
      <h2 className="playbook-build-review-title">
        {isCardinalityLoading ? <LoadingDots /> : targetingCardinality}{' '}
        {pluralize(targetingCardinality, 'person', 'people')} receive this email.
      </h2>
    }
    {isOneTimeEmailPlaybook &&
      playbookStatus === PlaybookStatus.SENT && (
        <h2 className="playbook-build-review-title">
          Up to {isCardinalityLoading ? <LoadingDots /> : targetingCardinality}{' '}
          {pluralize(targetingCardinality, 'person', 'people')} received this email.
        </h2>
      )}
    {isOutboundSequencePlaybook && <h2>You’re almost ready to send your sequence</h2>}
    {!isEmailPlaybook && <h2>Your playbook is ready.</h2>}
      <p dangerouslySetInnerHTML={{ __html: (isEmailPlaybook && !hasLimitedContactsMktEmail) ? HAS_UNLIMITED_CONTACTS_COPY : copy }} />
  </header>
)

const ReportLink = ({ isMarketingSequencePlaybook, playbookId }) => {
  const href = isMarketingSequencePlaybook ? `/reports/playbooks/${playbookId}` : `/reports/chat-playbooks/${playbookId}`
  return (
      <Button
        type="subtle"
        className="playbook-review-view-report-button"
        href={href}
      >
        View Report
      </Button>
    )
}

const Review = ({
  draft,
  copy,
  isOneTimeEmailPlaybook,
  isEmailPlaybook,
  isSendNow,
  isScheduled,
  onEnableConfirm,
  handleSetNow,
  handleSetScheduled,
  hasSegmentSelected,
  targetingCardinality,
  isCardinalityLoading,
  isOutboundSequencePlaybook,
  onShareClick,
  isGmailConnected,
  onConnectGmailClick,
  onInstallExtensionClick,
  userProperties,
  isMarketingSequencePlaybook,
  isDkimSetUp,
  isDkimVerified,
  hasLimitedContactsMktEmail
}) => (
    <div className="playbook-builder-review-wrapper">
      <ReviewHeader
        isEmailPlaybook={isEmailPlaybook}
        isOutboundSequencePlaybook={isOutboundSequencePlaybook}
        targetingCardinality={targetingCardinality}
        isCardinalityLoading={isCardinalityLoading}
        isOneTimeEmailPlaybook={isOneTimeEmailPlaybook}
        playbookStatus={draft.status}
        copy={copy}
        hasLimitedContactsMktEmail={hasLimitedContactsMktEmail}
      />
      {isOneTimeEmailPlaybook && (
        <div className="playbook-container">
          <div className={cx("playbook-review-email-send-options", { disabled: !hasSegmentSelected || (draft.status === PlaybookStatus.SENT || draft.status === PlaybookStatus.SCHEDULED) })}>
          <form className="playbook-review-email-send-options-form">
              <label className="email-send-radio-option"><Input type="radio" onClick={handleSetNow} disabled={!hasSegmentSelected || draft.status === PlaybookStatus.SENT } checked={isSendNow}/> Send email now</label>
              <label className="email-send-radio-option"><Input type="radio" onClick={handleSetScheduled} disabled={!hasSegmentSelected || draft.status === PlaybookStatus.SENT } checked={isScheduled}/> Schedule email for later</label>
            </form>
          </div>
          <hr/>
          {!hasSegmentSelected && (
            <div className="playbook-review-errors">
              <InlineSvg src={needsAttentionIconSrc} /> You must choose at least one segment to send email.{' '}
              <Link to={`/playbooks/${draft.id}/edit/targeting/audience`}>Build Your Audience</Link>
            </div>
          )}
          {isSendNow && (
            <SendButton
              className="playbook-review-email-send-button"
              onClickConfirm={onEnableConfirm}
              isMarketingSequencePlaybook={isMarketingSequencePlaybook}
              disabled={draft.status === PlaybookStatus.SENT}
            />
          )}
          {isScheduled && (
            <div>
            <ScheduleEmail />
            <ScheduleButton
                className="playbook-review-email-schedule-button"
                disabled={draft.status === PlaybookStatus.SENT}
              />
              <span className="playbook-review-schedule-warning">Heads up! You <strong>can't edit this email once </strong> it's been <strong>scheduled</strong></span>
            </div>
          )}
        </div>
      )}
      {!isOneTimeEmailPlaybook &&
        !isOutboundSequencePlaybook && (
          <div className="playbook-container playbook-review-toggle-summary">
            <div className="flex-space-between">
              <div className="color-navy-extra-dark dsg-medium-weight">This playbook is</div>
              <PlaybookDraftToggle />
            </div>
            {!isEmailPlaybook &&
              <PlaybookSelects reportType={draft.reportType} playbookId={draft.id}/>
            }
          </div>
        )}
        {isMarketingSequencePlaybook &&
          <InlineWarning
            title="Before you send this email"
            type="info"
            icon="info"
            className="playbook-review-warning">
            <div>
              {!isDkimSetUp &&
                <div className="playbook-review-warning--action-item">
                  <span className="playbook-review-warning--action-item-title">SET UP YOUR DKIM:</span> To make sure your emails are successfully delivered, <a href="/settings/dkim">set up DKIM</a> for your sending domain.
                </div>
              }
              {(isDkimSetUp && !isDkimVerified) &&
                <div className="playbook-review-warning--action-item">
                  <span className="playbook-review-warning--action-item-title">VERIFY YOUR DKIM:</span> To make sure your emails are successfully delivered, <a href="/settings/dkim">verify DKIM</a> for your sending domain.
                </div>
              }
            </div>
            <div className="playbook-review-warning--action-item">
              <span className="playbook-review-warning--action-item-title">TRACK YOUR REPLIES:</span> In order to track replies, make sure the senders of this email <a href="/settings/email">connect their Gmail accounts</a>.
            </div>
          </InlineWarning>
        }
      {!isOutboundSequencePlaybook &&
        [PlaybookStatus.ENABLED, PlaybookStatus.SENT].includes(draft.status) && (
          <ReportLink isMarketingSequencePlaybook={isMarketingSequencePlaybook} playbookId={draft.id} />
        )}
      {isOutboundSequencePlaybook && (
        <OutboundSequenceReview
          onShareClick={onShareClick}
          onConnectGmailClick={onConnectGmailClick}
          isGmailConnected={isGmailConnected}
          onInstallExtensionClick={onInstallExtensionClick}
          isExtensionInstalled={userProperties && userProperties.get('extensionInstalled')}
        />
      )}
    </div>
  )

export default Review



// WEBPACK FOOTER //
// ./src/scripts/components/playbook/components/Review/component.js